import { useState, useEffect } from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface UseThemeReturn {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first, fallback to system
    // Handle test environment where localStorage might not be available
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'system';
    }
    const stored = localStorage.getItem('koru-theme') as Theme;
    return stored || 'system';
  });

  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    // Handle test environment where matchMedia might not be available
    if (typeof window === 'undefined' || !window.matchMedia) {
      return 'light';
    }
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      return mediaQuery && mediaQuery.matches ? 'dark' : 'light';
    } catch (error) {
      return 'light';
    }
  });

  // Listen for system theme changes
  useEffect(() => {
    // Handle test environment where matchMedia might not be available
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      if (!mediaQuery) {
        return;
      }

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } catch (error) {
      // Silently fail in test environment
      return;
    }
  }, []);

  // Calculate resolved theme
  const resolvedTheme: 'light' | 'dark' = theme === 'system' ? systemTheme : theme;

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    // Handle test environment where localStorage might not be available
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('koru-theme', newTheme);
    }
  };

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
  };
} 